import {
  AnalysisSettings,
  FeaturePatternTrussSettings,
  LoadSettings,
  MaterialSelection,
  MaterialSettings,
  OrganizationPreset,
  ProfileDefaults,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';

type SettingsPresetTypes = {
  loadSettings: LoadSettings;
  analysisSettings: AnalysisSettings;
  materialSettings: MaterialSettings;
  featurePatternTrussSettings: FeaturePatternTrussSettings;
  profileDefaults: ProfileDefaults;
};

export type SettingsPresetType = keyof SettingsPresetTypes;
export type SettingsOfType<T extends SettingsPresetType> = SettingsPresetTypes[T];

export interface SettingsTypeMetadata<TSettings> {
  readonly key: SettingsPresetType;

  readonly label: string;

  readonly hasMaterialSelection: boolean;

  readonly fetch: () => Promise<OrganizationPreset<TSettings>[]>;

  readonly create: (
    name: string,
    settings: TSettings,
    materialSelection?: MaterialSelection,
  ) => Promise<OrganizationPreset<TSettings>>;

  readonly update: (
    guid: string,
    name: string,
    settings: TSettings,
    materialSelection?: MaterialSelection,
  ) => Promise<OrganizationPreset<TSettings>>;
}

type SettingsTypeRegistry = {
  [K in SettingsPresetType]: SettingsTypeMetadata<SettingsOfType<K>>;
};

const SETTINGS_TYPE_REGISTRY: SettingsTypeRegistry = {
  loadSettings: {
    key: 'loadSettings',
    label: 'Load Settings',
    hasMaterialSelection: false,
    fetch: designService.presets.getLoadSettingsPresets,
    create: designService.presets.createLoadSettingsPreset,
    update: designService.presets.updateLoadSettingsPreset,
  },

  analysisSettings: {
    key: 'analysisSettings',
    label: 'Analysis Settings',
    hasMaterialSelection: false,
    fetch: designService.presets.getAnalysisSettingsPresets,
    create: designService.presets.createAnalysisSettingsPreset,
    update: designService.presets.updateAnalysisSettingsPreset,
  },

  materialSettings: {
    key: 'materialSettings',
    label: 'Material Settings',
    hasMaterialSelection: true,
    fetch: designService.presets.getMaterialSettingsPresets,
    create: designService.presets.createMaterialSettingsPreset,
    update: designService.presets.updateMaterialSettingsPreset,
  },

  featurePatternTrussSettings: {
    key: 'featurePatternTrussSettings',
    label: 'Feature Pattern Truss Settings',
    hasMaterialSelection: false,
    fetch: designService.presets.getFeaturePatternTrussSettingsPresets,
    create: designService.presets.createFeaturePatternTrussSettingsPreset,
    update: designService.presets.updateFeaturePatternTrussSettingsPreset,
  },

  profileDefaults: {
    key: 'profileDefaults',
    label: 'Profile Defaults',
    hasMaterialSelection: false,
    fetch: designService.presets.getProfileDefaultsPresets,
    create: designService.presets.createProfileDefaultsPreset,
    update: designService.presets.updateProfileDefaultsPreset,
  },
};

export function settingsPresetsOfType<K extends SettingsPresetType>(key: K): SettingsTypeMetadata<SettingsOfType<K>> {
  return SETTINGS_TYPE_REGISTRY[key];
}

export function getAllSettingsTypesSupportingPresets(): SettingsPresetType[] {
  return Object.keys(SETTINGS_TYPE_REGISTRY) as SettingsPresetType[];
}
